/**
 @license
 Copyright (c) 2015-2026 Lablup Inc. All rights reserved.
 */
import { AppLaunchConfirmationModalFragment$key } from '../../__generated__/AppLaunchConfirmationModalFragment.graphql';
import { followLaunch, openAppWindow } from '../../helper/appWindow';
import { useBackendAIAppLauncher } from '../../hooks/useBackendAIAppLauncher';
import { Typography } from 'antd';
import { BAIButton, BAIFlex, BAIModal, BAIModalProps } from 'backend.ai-ui';
import { useTranslation } from 'react-i18next';
import { graphql, useFragment } from 'react-relay';

interface AppLaunchConfirmationModalProps extends BAIModalProps {
  sessionFrgmt: AppLaunchConfirmationModalFragment$key;
  appName: string;
  onRequestClose: () => void;
  port?: number;
  openToPublic?: boolean;
  allowedClientIps?: Array<string>;
}

/**
 * Confirmation modal for apps that require user acknowledgment before launching.
 * Used for nniboard and mlflow-ui apps that need to run before tunneling.
 *
 * This matches the legacy behavior of `_openAppLaunchConfirmationDialog`
 * from the former Lit-based app launcher component.
 */
const AppLaunchConfirmationModal: React.FC<AppLaunchConfirmationModalProps> = ({
  sessionFrgmt,
  appName,
  onRequestClose,
  port,
  openToPublic,
  allowedClientIps,
  ...modalProps
}) => {
  'use memo';

  const { t } = useTranslation();

  const session = useFragment(
    graphql`
      fragment AppLaunchConfirmationModalFragment on ComputeSessionNode {
        id
        row_id
        name
        ...useBackendAIAppLauncherFragment
      }
    `,
    sessionFrgmt,
  );

  const { launchAppWithNotification } = useBackendAIAppLauncher(session);

  const handleConfirmAndRun = async () => {
    // Open the app's window while the click still counts as a user gesture.
    const appWindow = openAppWindow();
    onRequestClose();

    // Open app in new window after preparation
    await followLaunch(
      appWindow,
      launchAppWithNotification({
        app: appName,
        port,
        openToPublic,
        allowedClientIps,
      }),
      (r) => r.appConnectUrl?.href,
      1000,
    );
  };

  return (
    <BAIModal
      title={t('session.appLauncher.AppMustBeRun')}
      onCancel={onRequestClose}
      footer={null}
      {...modalProps}
    >
      <BAIFlex direction="column" gap="md" align="stretch">
        <Typography.Paragraph>
          {t('session.appLauncher.AppMustBeRunDialog')}
        </Typography.Paragraph>
        <Typography.Paragraph>
          {t('dialog.ask.DoYouWantToProceed')}
        </Typography.Paragraph>

        <BAIButton
          type="primary"
          size="large"
          action={handleConfirmAndRun}
          block
        >
          {t('session.appLauncher.ConfirmAndRun')}
        </BAIButton>
      </BAIFlex>
    </BAIModal>
  );
};

export default AppLaunchConfirmationModal;
